import clsx from 'clsx';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CreateFileV2, FileListItem } from '../../File';
import { ElementsButton } from '../../UI';
import { File as GQLFile, FileUploadStatus } from '../../generated/graphql';
import { ESTATE_STATUS_ATTACHMENT_FALLBACK_CREATED_AT } from '../constants';
import type { EstateStatusAttachmentLink } from '../types';

function toAttachmentFile(attachmentLink: EstateStatusAttachmentLink, tenantID: string | undefined): GQLFile {
  const createdAt = attachmentLink.uploadedAt ?? ESTATE_STATUS_ATTACHMENT_FALLBACK_CREATED_AT;

  return {
    __typename: 'File',
    id: attachmentLink.id ?? attachmentLink.url,
    changeToken: '',
    contentType: '',
    createdAt,
    updatedAt: createdAt,
    createdBy: {
      elementsID: attachmentLink.uploadedByUserID,
      givenName: '',
      familyName: '',
    },
    downloadURL: attachmentLink.url,
    name: attachmentLink.fileName,
    standalone: true,
    tenantID: tenantID ?? '',
    uploadStatus: FileUploadStatus.Complete,
    uploadURL: null,
  };
}

export interface EstateStatusAttachmentDetailsProps {
  attachmentLinks: EstateStatusAttachmentLink[];
  isDisabled?: boolean;
  tenantID?: string;
  onFileCreation?: (file: GQLFile) => void;
  onRemoveAttachment?: (attachmentLink: EstateStatusAttachmentLink, index: number) => void;
  emptyState?: ReactNode;
}

function EstateStatusAttachmentRow({
  attachmentLink,
  index,
  isDisabled,
  onRemoveAttachment,
  tenantID,
}: {
  attachmentLink: EstateStatusAttachmentLink;
  index: number;
  isDisabled?: boolean;
  tenantID?: string;
  onRemoveAttachment?: (attachmentLink: EstateStatusAttachmentLink, index: number) => void;
}) {
  const { t } = useTranslation('estateStatus');
  const attachmentFile = toAttachmentFile(attachmentLink, tenantID);
  const shouldRefreshDownloadURL = !!attachmentLink.id && !attachmentLink.id.startsWith('estate-status-attachment-');

  return (
    <div
      className={clsx(
        'grid gap-3 rounded-2xl p-3 sm:grid-cols-[minmax(0,1fr)_auto]',
        'border border-darkMode-gray-medium/20 bg-darkMode-black/60'
      )}
    >
      <FileListItem
        className="min-w-0 !bg-transparent !p-0"
        file={attachmentFile}
        tenantID={tenantID ?? ''}
        itemID={undefined}
        shouldRefreshDownloadURL={shouldRefreshDownloadURL}
        subtitle={false}
      />

      {onRemoveAttachment ? (
        <div className="flex items-start sm:justify-end">
          <ElementsButton
            type="button"
            theme="danger"
            size="small"
            className="w-full sm:w-auto"
            disabled={isDisabled}
            label={t('attachments-remove')}
            onClick={() => onRemoveAttachment(attachmentLink, index)}
          />
        </div>
      ) : null}
    </div>
  );
}

export function EstateStatusAttachmentDetails({
  attachmentLinks,
  isDisabled,
  tenantID,
  onFileCreation,
  onRemoveAttachment,
  emptyState,
}: EstateStatusAttachmentDetailsProps) {
  const { t } = useTranslation('estateStatus');
  const resolvedEmptyState = emptyState ?? t('attachments-empty');
  const uploadAction =
    tenantID && onFileCreation ? (
      <CreateFileV2
        tenantID={tenantID}
        standalone
        label={t('attachments-upload')}
        theme="secondary"
        size="small"
        onFileCreation={onFileCreation}
      />
    ) : null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-medium uppercase tracking-widest text-darkMode-gray-light/75">
          {t('attachments-heading')}
        </span>
        {uploadAction}
      </div>

      {attachmentLinks.length === 0 ? (
        <p className="text-sm text-darkMode-gray-light/70">{resolvedEmptyState}</p>
      ) : (
        <div className="space-y-3">
          {attachmentLinks.map((attachmentLink, index) => (
            <EstateStatusAttachmentRow
              key={attachmentLink.id ?? `${attachmentLink.fileName}-${index}`}
              attachmentLink={attachmentLink}
              index={index}
              tenantID={tenantID}
              isDisabled={isDisabled}
              onRemoveAttachment={onRemoveAttachment}
            />
          ))}
        </div>
      )}
    </div>
  );
}
